import React, { useState, useCallback } from 'react';
import { fr } from '../i18n/fr';
import { ModeIndicator, type PlaybackMode } from './ModeIndicator';
import { QualitySelector, type QualityLevel, type QualityOption } from './QualitySelector';

export interface ControlBarProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
  onVolumeChange: (volume: number) => void;
  onToggleMute: () => void;
  isMuted: boolean;
  volume: number;
  mode: PlaybackMode;
  currentQuality: QualityLevel;
  onQualityChange: (option: QualityOption) => void;
}

/**
 * Control bar with play/pause, volume, and fullscreen controls.
 * No seek bar — this is a live linear experience (Req 1.4).
 */
export const ControlBar: React.FC<ControlBarProps> = ({
  isPlaying,
  onTogglePlay,
  onVolumeChange,
  onToggleMute,
  isMuted,
  volume,
  mode,
  currentQuality,
  onQualityChange,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(() => {});
    }
  }, []);

  return (
    <div style={styles.container} role="toolbar" aria-label="Contrôles du lecteur">
      {/* Left section: Play/Pause + Mode Indicator */}
      <div style={styles.leftSection}>
        <button
          onClick={onTogglePlay}
          style={styles.button}
          aria-label={isPlaying ? fr.controls.pause : fr.controls.play}
          title={isPlaying ? fr.controls.pause : fr.controls.play}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>

        <ModeIndicator mode={mode} />
      </div>

      {/* Right section: Volume + Fullscreen */}
      <div style={styles.rightSection}>
        <button
          onClick={onToggleMute}
          style={styles.button}
          aria-label={isMuted ? fr.controls.unmute : fr.controls.mute}
          title={isMuted ? fr.controls.unmute : fr.controls.mute}
        >
          {isMuted || volume === 0 ? <MuteIcon /> : <VolumeIcon />}
        </button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={isMuted ? 0 : volume}
          onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
          style={styles.volumeSlider}
          aria-label="Volume"
        />

        <QualitySelector
          currentLevel={currentQuality}
          onQualityChange={onQualityChange}
        />

        <button
          onClick={toggleFullscreen}
          style={styles.button}
          aria-label={isFullscreen ? fr.controls.exitFullscreen : fr.controls.fullscreen}
          title={isFullscreen ? fr.controls.exitFullscreen : fr.controls.fullscreen}
        >
          {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
        </button>
      </div>
    </div>
  );
};

// Simple SVG icons
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const VolumeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
  </svg>
);

const MuteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const FullscreenIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

const ExitFullscreenIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    color: '#ffffff',
    transition: 'background 0.2s',
  },
  volumeSlider: {
    width: '80px',
    height: '4px',
    cursor: 'pointer',
    accentColor: '#006B2B',
  },
};
